/**
 * api/jit.ts — endpoints específicos do backend Aura JIT.
 */

import { apiFetch } from './client';
import type {
  AccountBootstrapPayload,
  AccountBootstrapResponse,
  BootstrapPlanResponse,
  BreakGlassWindow,
  BreakGlassWindowClosePayload,
  BreakGlassWindowCreatePayload,
  BreakGlassWindowsList,
  AccountCreatePayload,
  AccountUpdatePayload,
  AccountItem,
  AuditList,
  ClientCreatePayload,
  ClientDetail,
  ClientItem,
  ClientList,
  ClientLogoUploadRequest,
  ClientLogoUploadResponse,
  ClientUpdatePayload,
  ConsoleUrlResponse,
  CreateRequestPayload,
  EligibilityResponse,
  IdcGroupsList,
  IdcUser,
  IdcUserCreatePayload,
  IdcUserDeleteResponse,
  IdcUserGroupsUpdatePayload,
  IdcUsersList,
  RejectRequestPayload,
  RequestItem,
  RequestsList,
  VerifyBootstrapUpdateResponse,
} from './types';

export const jitApi = {
  // GET /eligibility/me
  getEligibility: (signal?: AbortSignal) =>
    apiFetch<EligibilityResponse>('/eligibility/me', { signal }),

  // GET /requests/me?limit=&next=
  listMyRequests: (params?: { limit?: number; next?: string }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.next) qs.set('next', params.next);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<RequestsList>(`/requests/me${suffix}`, { signal });
  },

  // POST /requests
  createRequest: (payload: CreateRequestPayload) =>
    apiFetch<RequestItem>('/requests', { method: 'POST', body: payload }),

  // POST /requests/{id}/cancel — owner cancela própria pendente
  cancelMyRequest: (requestId: string) =>
    apiFetch<RequestItem>(`/requests/${encodeURIComponent(requestId)}/cancel`, {
      method: 'POST',
      body: {},
    }),

  // GET /approvals/pending?limit=&next=
  listPendingApprovals: (params?: { limit?: number; next?: string }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.next) qs.set('next', params.next);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<RequestsList>(`/approvals/pending${suffix}`, { signal });
  },

  // POST /approvals/{id}/approve
  approveRequest: (requestId: string) =>
    apiFetch<RequestItem>(`/approvals/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      body: {},
    }),

  // POST /approvals/{id}/reject
  rejectRequest: (requestId: string, payload: RejectRequestPayload) =>
    apiFetch<RequestItem>(`/approvals/${encodeURIComponent(requestId)}/reject`, {
      method: 'POST',
      body: payload,
    }),

  // GET /audit?date=YYYY-MM-DD&...
  queryAudit: (
    params: { date: string; event_type?: string; actor?: string; limit?: number; next?: string },
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams();
    qs.set('date', params.date);
    if (params.event_type) qs.set('event_type', params.event_type);
    if (params.actor) qs.set('actor', params.actor);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.next) qs.set('next', params.next);
    return apiFetch<AuditList>(`/audit?${qs}`, { signal });
  },

  // ---------------------------------------------------------------------------
  // W1+W2 iam-client-portfolio
  // ---------------------------------------------------------------------------

  // GET /clients — lista clientes visíveis ao caller
  listClients: (signal?: AbortSignal) => apiFetch<ClientList>('/clients', { signal }),

  // GET /clients/{slug} — detalhe + accounts
  getClient: (slug: string, signal?: AbortSignal) =>
    apiFetch<ClientDetail>(`/clients/${encodeURIComponent(slug)}`, { signal }),

  // POST /clients — TEAMAdmin only
  createClient: (payload: ClientCreatePayload) =>
    apiFetch<ClientItem>('/clients', { method: 'POST', body: payload }),

  // PATCH /clients/{slug} — TEAMAdmin only
  updateClient: (slug: string, payload: ClientUpdatePayload) =>
    apiFetch<ClientItem>(`/clients/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: payload,
    }),

  // POST /clients/{slug}/logo-upload-url — TEAMAdmin only
  createClientLogoUploadUrl: (slug: string, payload: ClientLogoUploadRequest) =>
    apiFetch<ClientLogoUploadResponse>(`/clients/${encodeURIComponent(slug)}/logo-upload-url`, {
      method: 'POST',
      body: payload,
    }),

  // POST presigned S3 form — no Aura auth header, no JSON wrapper.
  uploadClientLogoFile: async (upload: ClientLogoUploadResponse, file: File) => {
    const formData = new FormData();
    Object.entries(upload.upload_fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);
    const response = await fetch(upload.upload_url, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`Falha no upload da logo (${response.status}).`);
    }
  },

  // POST /clients/{slug}/accounts — TEAMAdmin only
  addAccount: (slug: string, payload: AccountCreatePayload) =>
    apiFetch<AccountItem>(`/clients/${encodeURIComponent(slug)}/accounts`, {
      method: 'POST',
      body: payload,
    }),

  // PATCH /clients/{slug}/accounts/{accountId} — TEAMAdmin only.
  // Edita só metadados (name, environment, notes). account_id imutável.
  updateAccount: (slug: string, accountId: string, payload: AccountUpdatePayload) =>
    apiFetch<AccountItem>(
      `/clients/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}`,
      { method: 'PATCH', body: payload },
    ),

  // DELETE /clients/{slug}/accounts/{accountId} — TEAMAdmin only
  removeAccount: (slug: string, accountId: string) =>
    apiFetch<void>(
      `/clients/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}`,
      { method: 'DELETE' },
    ),

  // ---------------------------------------------------------------------------
  // iam-client-cross-account-jumper (W3+W4)
  // ---------------------------------------------------------------------------

  // POST /clients/{slug}/accounts/{accountId}/bootstrap — TEAMAdmin only
  // Engenheiro Aura cola outputs do CFN stack do cliente + ExternalId.
  bootstrapAccount: (slug: string, accountId: string, payload: AccountBootstrapPayload) =>
    apiFetch<AccountBootstrapResponse>(
      `/clients/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}/bootstrap`,
      { method: 'POST', body: payload },
    ),

  // GET /clients/{slug}/accounts/{accountId}/bootstrap-plan — TEAMAdmin only
  // Read-only template update handoff. Does not rotate ExternalId.
  getBootstrapPlan: (slug: string, accountId: string, signal?: AbortSignal) =>
    apiFetch<BootstrapPlanResponse>(
      `/clients/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}/bootstrap-plan`,
      { signal },
    ),

  // POST /clients/{slug}/accounts/{accountId}/verify-update — TEAMAdmin only
  // Verifies the stored provisioner role with the stored ExternalId. Does not rotate secrets.
  verifyBootstrapUpdate: (slug: string, accountId: string) =>
    apiFetch<VerifyBootstrapUpdateResponse>(
      `/clients/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(accountId)}/verify-update`,
      { method: 'POST', body: {} },
    ),

  // POST /grants/{requestId}/console-url — owner ou TEAMAdmin
  // Gera URL federada AWS console pra grant cross-account válido.
  getConsoleUrl: (requestId: string) =>
    apiFetch<ConsoleUrlResponse>(`/grants/${encodeURIComponent(requestId)}/console-url`, {
      method: 'POST',
      body: {},
    }),

  // POST /accounts/{accountId}/readonly-console — always-on, qualquer user logado
  // Gera URL federada AWS console pra role `aura` (ReadOnly). Sem flow de
  // aprovacao. Conta precisa estar bootstrapada (readonly_role_arn populado).
  getReadonlyConsoleUrl: (accountId: string) =>
    apiFetch<ConsoleUrlResponse>(`/accounts/${encodeURIComponent(accountId)}/readonly-console`, {
      method: 'POST',
      body: {},
    }),

  // ---------------------------------------------------------------------------
  // W8 admin-revoke-grant
  // ---------------------------------------------------------------------------

  // GET /admin/grants?limit=&next= — TEAMAdmins/TEAMAuditors
  listActiveGrants: (params?: { limit?: number; next?: string }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.next) qs.set('next', params.next);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<RequestsList>(`/admin/grants${suffix}`, { signal });
  },

  // POST /admin/grants/{id}/revoke — TEAMAdmins ou requester dono do grant ativo
  revokeGrant: (requestId: string, reason?: string) =>
    apiFetch<RequestItem>(`/admin/grants/${encodeURIComponent(requestId)}/revoke`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),

  // Self-revoke usa a mesma rota até existir alias público /requests/{id}/revoke.
  revokeMyGrant: (requestId: string) =>
    apiFetch<RequestItem>(`/admin/grants/${encodeURIComponent(requestId)}/revoke`, {
      method: 'POST',
      body: { reason: 'Revogado pelo próprio solicitante' },
    }),

  // ---------------------------------------------------------------------------
  // PR #C4 IdC user management (TEAMAdmins; list endpoints também Auditors)
  // ---------------------------------------------------------------------------

  // GET /admin/users?limit=&next_token=
  listIdcUsers: (params?: { limit?: number; next_token?: string }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.next_token) qs.set('next_token', params.next_token);
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<IdcUsersList>(`/admin/users${suffix}`, { signal });
  },

  // GET /admin/groups
  listIdcGroups: (signal?: AbortSignal) => apiFetch<IdcGroupsList>('/admin/groups', { signal }),

  // GET /admin/breakglass/windows?status=open|closed
  listBreakGlassWindows: (
    params?: { status?: 'open' | 'closed'; limit?: number },
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiFetch<BreakGlassWindowsList>(`/admin/breakglass/windows${suffix}`, { signal });
  },

  // POST /admin/breakglass/windows
  createBreakGlassWindow: (payload: BreakGlassWindowCreatePayload) =>
    apiFetch<BreakGlassWindow>('/admin/breakglass/windows', { method: 'POST', body: payload }),

  // POST /admin/breakglass/windows/{id}/close
  closeBreakGlassWindow: (windowId: string, payload: BreakGlassWindowClosePayload) =>
    apiFetch<BreakGlassWindow>(`/admin/breakglass/windows/${encodeURIComponent(windowId)}/close`, {
      method: 'POST',
      body: payload,
    }),

  // POST /admin/users
  createIdcUser: (payload: IdcUserCreatePayload) =>
    apiFetch<IdcUser>('/admin/users', { method: 'POST', body: payload }),

  // PATCH /admin/users/{user_id}/groups
  updateIdcUserGroups: (userId: string, payload: IdcUserGroupsUpdatePayload) =>
    apiFetch<IdcUser>(`/admin/users/${encodeURIComponent(userId)}/groups`, {
      method: 'PATCH',
      body: payload,
    }),

  // DELETE /admin/users/{user_id} — idempotente
  deleteIdcUser: (userId: string) =>
    apiFetch<IdcUserDeleteResponse>(`/admin/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
};
